import { Link } from 'react-router-dom';
import { ExportReviewFeedbackButton } from '@features/pbqa-eval';
import type { EvalConfigListItem } from '@queries/evalConfigs';
import { formatDate } from '@root/helpers';
import { type CellContext, createColumnHelper } from '@tanstack/react-table';

import { mutedBadgeClasses, warnBadgeClasses } from '../classes';
import { WORKFLOW_TYPE_LABELS } from '../constants';

const columnHelper = createColumnHelper<EvalConfigListItem>();

interface EvalConfigsTableColumnsOptions {
  /** Promotes the row's config to champion. */
  onSetChampion: (id: string) => void;
  /** Id of the config whose promotion is in flight, if any. */
  settingChampionId?: string;
}

export const getEvalConfigsTableColumns = ({ onSetChampion, settingChampionId }: EvalConfigsTableColumnsOptions) => [
  columnHelper.accessor('id', {
    id: 'id',
    enableColumnFilter: false,
    enableHiding: false,
  }),
  columnHelper.accessor((row) => row.name, {
    id: 'name',
    header: 'Name',
    sortingFn: 'text',
    sortUndefined: -1,
    enableColumnFilter: false,
    size: 260,
    minSize: 200,
    cell: (ctx: CellContext<EvalConfigListItem, string>) => (
      <Link
        to={`/eval-configs/${ctx.row.original.id}`}
        className="text-primary-600 hover:text-primary-900 font-medium transition-colors"
      >
        {ctx.getValue() || 'Untitled'}
      </Link>
    ),
    meta: { tooltip: true, dataCellClassName: 'text-sm leading-5 font-medium' },
  }),
  columnHelper.accessor((row) => row.workflow_type, {
    id: 'workflow_type',
    header: 'Workflow',
    filterFn: 'arrIncludesSome',
    size: 180,
    cell: (ctx: CellContext<EvalConfigListItem, string>) => (
      <span className={mutedBadgeClasses}>
        {WORKFLOW_TYPE_LABELS[ctx.getValue() as keyof typeof WORKFLOW_TYPE_LABELS] ?? ctx.getValue()}
      </span>
    ),
    meta: { filter: { type: 'dropdown', placeholder: 'All workflows' } },
  }),
  columnHelper.accessor((row) => row.current_version_number ?? 0, {
    id: 'current_version_number',
    header: 'Version',
    enableColumnFilter: false,
    size: 110,
    cell: (ctx: CellContext<EvalConfigListItem, number>) => {
      const v = ctx.row.original.current_version_number;
      return v ? <span>v{v}</span> : <span className="text-info-400">—</span>;
    },
  }),
  columnHelper.accessor((row) => (row.has_unpublished_draft_changes ? 'yes' : 'no'), {
    id: 'has_unpublished_draft_changes',
    header: 'Status',
    filterFn: 'arrIncludesSome',
    size: 180,
    cell: (ctx: CellContext<EvalConfigListItem, string>) =>
      ctx.row.original.archived ? (
        <span className={mutedBadgeClasses}>Archived</span>
      ) : ctx.row.original.has_unpublished_draft_changes ? (
        <span className={warnBadgeClasses}>Unpublished changes</span>
      ) : ctx.row.original.current_version_number ? (
        <span className={mutedBadgeClasses}>Published</span>
      ) : (
        <span className={mutedBadgeClasses}>Draft only</span>
      ),
    meta: { filter: { type: 'dropdown', placeholder: 'All statuses' } },
  }),
  /**
   * Champion = the one config brokers see runs of. Everything else is a
   * challenger internal staff can re-run freely without brokers noticing,
   * so promoting one is a deliberate, per-row action rather than a toggle.
   */
  columnHelper.accessor((row) => (row.is_champion ? 'yes' : 'no'), {
    id: 'is_champion',
    header: 'Broker view',
    filterFn: 'arrIncludesSome',
    size: 170,
    cell: (ctx: CellContext<EvalConfigListItem, string>) =>
      ctx.row.original.is_champion ? (
        <span className={mutedBadgeClasses}>Champion</span>
      ) : (
        <button
          type="button"
          data-testid={`eval-config-set-champion-${ctx.row.original.id}`}
          disabled={Boolean(settingChampionId)}
          onClick={() => onSetChampion(ctx.row.original.id)}
          className="text-primary-600 hover:text-primary-900 cursor-pointer font-light transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          Set as champion
        </button>
      ),
    meta: { filter: { type: 'dropdown', placeholder: 'All configs' } },
  }),
  columnHelper.accessor((row) => row.created_by?.name, {
    id: 'created_by',
    header: 'Created by',
    size: 180,
    filterFn: 'arrIncludesSome',
    cell: (ctx: CellContext<EvalConfigListItem, string | undefined>) => ctx.getValue() ?? '—',
    meta: { filter: { type: 'dropdown', placeholder: 'All users' } },
  }),
  columnHelper.accessor((row) => row.last_updated_by?.name, {
    id: 'last_updated_by',
    header: 'Updated by',
    size: 180,
    filterFn: 'arrIncludesSome',
    cell: (ctx: CellContext<EvalConfigListItem, string | undefined>) => ctx.getValue() ?? '—',
    meta: { filter: { type: 'dropdown', placeholder: 'All users' } },
  }),
  columnHelper.accessor((row) => row.updated_at ?? row.created_at, {
    id: 'updated_at',
    header: 'Updated at',
    enableColumnFilter: false,
    size: 168,
    cell: (ctx: CellContext<EvalConfigListItem, string>) => formatDate(ctx.getValue(), 'DD MMM YYYY HH:mm'),
    sortingFn: 'datetime',
    sortUndefined: -1,
  }),
  columnHelper.accessor((row) => row.created_at, {
    id: 'created_at',
    header: 'Created at',
    enableColumnFilter: false,
    size: 168,
    cell: (ctx: CellContext<EvalConfigListItem, string>) => formatDate(ctx.getValue(), 'DD MMM YYYY HH:mm'),
    sortingFn: 'datetime',
    sortUndefined: -1,
  }),
  /**
   * One CSV per config: the review-triage unit is "how did this config's
   * runs do", so the export is a row action rather than a page-level one.
   */
  columnHelper.display({
    id: 'export_runs',
    header: '',
    enableHiding: false,
    enableColumnFilter: false,
    size: 110,
    minSize: 110,
    maxSize: 110,
    cell: (ctx: CellContext<EvalConfigListItem, unknown>) => (
      <ExportReviewFeedbackButton configId={ctx.row.original.id} configName={ctx.row.original.name || 'config'} />
    ),
    meta: {
      dataCellClassName: 'relative truncate whitespace-nowrap px-1 py-0 text-center text-sm font-medium',
    },
  }),
  columnHelper.display({
    id: 'view',
    header: '',
    enableHiding: false,
    enableColumnFilter: false,
    size: 65,
    minSize: 65,
    maxSize: 65,
    cell: (ctx: CellContext<EvalConfigListItem, unknown>) => (
      <Link
        to={`/eval-configs/${ctx.row.original.id}`}
        className="text-primary-600 hover:text-primary-900 cursor-pointer truncate font-light transition-colors"
      >
        View
      </Link>
    ),
    meta: {
      dataCellClassName: 'relative truncate whitespace-nowrap px-1 py-0 text-center text-sm font-medium',
    },
  }),
];
